import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../../ui/toast';
import { getEmployeeList } from '../../../../../../../utils/api_service/employees';
import {
  getInterviewRounds,
  getJobOpeningDetails,
} from '../../../../../../../utils/api_service/JobOpenings';
import {
  ITimeSlot,
  createInterviewRound,
  getInterviewRound,
  updateInterviewRound,
} from '../../../../../../../utils/api_service/SmartInterview';
import useDebouncedEmployeeSearch from '../../../../../../../utils/helpers/AsyncSelect/EmployeeGet';
import { EmployeeUser } from '../../../../../../../utils/types/admin_types';
import { JobOpeningDetail } from '../../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import email_template from '../../InvitationTemplates/email_template.json';
import { renderTipTapJsonToHtml } from './renderTipTap';

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

export const useConfigurations = ({
  interviewId,
  setInterviewId,
  onRoundCreated,
}: {
  interviewId: string;
  setInterviewId: (id: string) => void;
  onRoundCreated: () => void;
}) => {
  const { id: jobOpeningIdFromUrl } = useParams<{ id: string }>();
  const [interviewData, setInterviewData] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [jobOpeningId, setJobOpeningId] = useState('');
  const [host, setHost] = useState<SelectOptions>();
  const [panelists, setPanelists] = useState<SelectOptions[]>([]);
  const [timeSlot, setTimeSlot] = useState<ITimeSlot[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedTimes, setSelectedTimes] = useState<{
    [key: string]: string[];
  }>({});
  const [defaultEmployeeUserList, setDefaultEmployeeUserList] = useState<
    EmployeeUser[]
  >([]);
  const [isChanged, setIsChanged] = useState(false);
  const { loadOptionsDebounced } = useDebouncedEmployeeSearch();
  const [errors, setErrors] = useState({
    title: false,
    host: false,
    panelists: false,
    selectedDates: false,
    selectedTimes: false,
  });
  const [touched, setTouched] = useState({
    title: false,
    host: false,
    panelists: false,
    selectedDates: false,
    selectedTimes: false,
  });

  const validateForm = (forceTouch = false) => {
    const hasTime = Object.values(selectedTimes).some(
      (times) => times.length > 0,
    );
    const newErrors = {
      title: !title.trim(),
      host: !host,
      panelists: panelists.length === 0,
      selectedDates: selectedDates.length === 0,
      selectedTimes: !hasTime,
    };
    setErrors((prev) => ({ ...prev, ...newErrors }));
    // Only mark all fields as touched if forceTouch is enabled (e.g. for submission)
    if (forceTouch) {
      setTouched({
        title: true,
        host: true,
        panelists: true,
        selectedDates: true,
        selectedTimes: true,
      });
    }
    return !Object.values(newErrors).includes(true);
  };

  const generateTime = (): ITimeSlot[] => {
    const slots: ITimeSlot[] = [];
    selectedDates.forEach((date) => {
      const key = formatDateKey(date);
      const array = selectedTimes[key] || [];
      array.forEach((time) => {
        const [h, m] = time.split(':').map(Number);
        const start = new Date(date);
        start.setHours(h, m, 0, 0);
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        slots.push({
          startTime: Math.floor(start.getTime() / 1000),
          endTime: Math.floor(end.getTime() / 1000),
          isBooked: false,
        });
      });
    });
    return slots;
  };

  const createInterview = async () => {
    if (!validateForm(true)) {
      toast.warning('Please fill in all fields', {
        position: 'bottom-center',
      });
      return;
    }
    const timeSlots = generateTime();
    const defaultSchedulingJson = email_template.scheduling.defaultJson;
    const defaultMeetingJson = email_template.meetingDetails.defaultJson;
    const sHtml = renderTipTapJsonToHtml(defaultSchedulingJson);
    const mHtml = renderTipTapJsonToHtml(defaultMeetingJson);
    try {
      const res = await createInterviewRound({
        title,
        jobOpeningId: jobOpeningIdFromUrl || jobOpeningId,
        host: host!.value,
        panelists: panelists.map((p: SelectOptions) => p.value),
        timeSlots,
        schedulingTemplateHtml: sHtml,
        schedulingTemplateJson: JSON.stringify(defaultSchedulingJson),
        meetingDetailsTemplateHtml: mHtml,
        meetingDetailsTemplateJson: JSON.stringify(defaultMeetingJson),
        meetingDuration: 30,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Interview Round Created!', {
        position: 'bottom-center',
      });
      setInterviewId(data.id);
      onRoundCreated();
    } catch (e) {
      toast.error((e as Error).message, {
        position: 'bottom-center',
      });
    }
  };

  const updateInterview = async (
    schedulingTemplateHtml: string,
    schedulingTemplateJson: string,
    meetingTemplateHtml: string,
    meetingTemplateJson: string,
  ) => {
    if (!validateForm(true)) {
      toast.warning('Please fill in all fields', {
        position: 'bottom-center',
      });
      return;
    }
    const timeSlots = generateTime();
    try {
      const res = await updateInterviewRound(interviewId, {
        title,
        jobOpeningId: jobOpeningIdFromUrl || jobOpeningId,
        host: host!.value,
        panelists: panelists.map((p: SelectOptions) => p.value),
        timeSlots,
        schedulingTemplateHtml,
        schedulingTemplateJson,
        meetingDetailsTemplateHtml: meetingTemplateHtml,
        meetingDetailsTemplateJson: meetingTemplateJson,
        meetingDuration: 30,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Interview Round Updated!', {
        position: 'bottom-center',
      });
      setInterviewId(data.id);
    } catch (e) {
      toast.error((e as Error).message, {
        position: 'bottom-center',
      });
    }
  };

  useEffect(() => {
    getEmployeeList('', 1, 10)
      .then((r) =>
        r.ok ? r.json() : Promise.reject(r.json().then((e) => e.error)),
      )
      .then((d) => setDefaultEmployeeUserList(d.employee))
      .catch((e) => toast.error(e, { position: 'bottom-center' }));
  }, []);

  const defaultEmployeeUserOptions = defaultEmployeeUserList.map((emp) => ({
    value: emp.id,
    label: emp.user.firstName + ' ' + emp.user.lastName,
  }));

  const handleHostChange = (o: SelectOptions) => {
    setHost(o);
    setIsChanged(true);
  };
  const handlePanelists = (arr: SelectOptions[]) => {
    setPanelists(arr);
    setIsChanged(true);
  };

  const handleDateSelection = (dates: Date[]) => {
    const keys = dates.map(formatDateKey);
    const filtered = Object.fromEntries(
      Object.entries(selectedTimes).filter(([k]) => keys.includes(k)),
    );
    setSelectedDates(dates);
    setSelectedTimes(filtered);
    setIsChanged(true);
  };

  const handleTimeSlotSelection = (date: Date, time: string) => {
    const key = formatDateKey(date);
    setSelectedTimes((prev) => {
      const arr = prev[key] || [];
      const updated = arr.includes(time)
        ? arr.filter((x) => x !== time)
        : [...arr, time].sort();
      return { ...prev, [key]: updated };
    });
    setIsChanged(true);
  };

  // Memoize validation result to prevent unnecessary re-renders
  const isFormValid = useMemo(() => {
    const hasTime = Object.values(selectedTimes).some(
      (times) => times.length > 0,
    );
    return !!(
      title.trim() &&
      host &&
      panelists.length > 0 &&
      selectedDates.length > 0 &&
      hasTime
    );
  }, [title, host, panelists, selectedDates, selectedTimes]);

  useEffect(() => {
    if (!interviewId) return;
    getInterviewRound(interviewId)
      .then((r) => r.json())
      .then((d) => {
        setInterviewData(d);
        setTitle(d.title || '');
        if (d.host) {
          handleHostChange({
            value: d.host.id,
            label: d.host.user.firstName + ' ' + d.host.user.lastName,
          });
        }
        if (Array.isArray(d.panelists)) {
          handlePanelists(
            d.panelists.map((p: any) => ({
              value: p.id,
              label: p.user.firstName + ' ' + p.user.lastName,
            })),
          );
        }
        if (Array.isArray(d.timeSlots)) {
          const dateMap = new Map<string, Date>();
          const timesMap: {
            [key: string]: string[];
          } = {};
          d.timeSlots.forEach((slot: ITimeSlot) => {
            const dt = new Date(slot.startTime * 1000);
            const key = formatDateKey(dt);
            dateMap.set(key, dt);
            timesMap[key] = timesMap[key] || [];
            const h = dt.getHours().toString().padStart(2, '0');
            const m = dt.getMinutes().toString().padStart(2, '0');
            timesMap[key].push(h + ':' + m);
          });
          handleDateSelection(Array.from(dateMap.values()));
          setSelectedTimes(timesMap);
        }
      })
      .catch(console.error);
  }, [interviewId]);
  useEffect(() => {
    if (!interviewData) return;

    const updateFormData = () => {
      // Title update
      if (interviewData.title) {
        setTitle(interviewData.title);
      }

      // Host update
      if (interviewData.host) {
        const formattedHost = {
          label: `${interviewData.host.user.firstName} ${interviewData.host.user.lastName}`,
          value: interviewData.host.id,
          user: interviewData.host.user,
          ...interviewData.host,
        };
        handleHostChange(formattedHost);
      }

      // Panelist update
      if (interviewData.panelists || interviewData.penelists) {
        const panelists = interviewData.penelists || interviewData.panelists;

        if (Array.isArray(panelists)) {
          const formattedPanelists = panelists.map((panelist) => ({
            label: `${panelist.user.firstName} ${panelist.user.lastName}`,
            value: panelist.id,
            user: panelist.user,
            ...panelist,
          }));
          handlePanelists(formattedPanelists);
        }
      }
    };

    updateFormData();
  }, [interviewData]); // Only depend on interviewData

  // Handle time slots separately
  useEffect(() => {
    if (!interviewData?.timeSlots?.length) return;

    const processTimeSlots = () => {
      const dateMap = new Map<string, { date: Date; times: string[] }>();

      // Process all time slots first
      interviewData.timeSlots.forEach((slot: any) => {
        const date = new Date(slot.startTime * 1000);
        const dateKey = date.toISOString().split('T')[0];

        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { date, times: [] });
        }

        const timeString = date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

        dateMap.get(dateKey)!.times.push(timeString);
      });

      // Set all dates at once
      const dates = Array.from(dateMap.values()).map((item) => item.date);
      handleDateSelection(dates);

      // Then set all times
      dateMap.forEach(({ date, times }) => {
        times.forEach((time) => handleTimeSlotSelection(date, time));
      });
    };

    processTimeSlots();
  }, [interviewData?.timeSlots]); // Only depend on timeSlots changes
  const [jobTitle, setJobTitle] = useState('');

  useEffect(() => {
    let isSubscribed = true;

    const loadJobDetailsAndGenerateTitle = async () => {
      try {
        const res = await getJobOpeningDetails(jobOpeningId);
        if (!res.ok) throw new Error('Failed to fetch job details');
        const data: JobOpeningDetail = await res.json();
        const jobBaseTitle =
          typeof data.jobDescription?.jobTitle === 'string'
            ? data.jobDescription.jobTitle
            : data.jobDescription?.jobTitle?.jobTitle;

        if (!jobBaseTitle || !isSubscribed) return;

        setJobTitle(jobBaseTitle);

        // Fetch existing rounds to generate next round title
        const roundsRes = await getInterviewRounds(jobOpeningId);
        const rounds = await roundsRes.json();

        // Only generate title for new rounds (when title is empty or default)
        // If we have interview data or title is already set, don't overwrite it
        if (!interviewData && !title) {
          const nextRoundNumber = rounds.length + 1;
          const roundTitle = `${jobBaseTitle} - Round ${nextRoundNumber}`;

          if (isSubscribed) {
            setTitle(roundTitle);
          }
        }
      } catch (e) {
        console.error('Error generating round title:', e);
      }
    };

    loadJobDetailsAndGenerateTitle();

    return () => {
      isSubscribed = false;
    };
  }, [jobOpeningId, title]); // Add title as dependency to prevent overwriting

  const disabledSlots = useMemo(() => {
    const slots = interviewData?.timeSlots as ITimeSlot[] | undefined;
    if (!slots) return {};
    return slots.reduce((map, slot) => {
      if (!slot.isBooked) return map;
      const dt = new Date(slot.startTime * 1000);
      const key = formatDateKey(dt);
      const hh = dt.getHours().toString().padStart(2, '0');
      const mm = dt.getMinutes().toString().padStart(2, '0');
      const timeStr = `${hh}:${mm}`;
      if (map[key]) {
        map[key].push(timeStr);
      } else {
        map[key] = [timeStr];
      }
      return map;
    }, {} as Record<string, string[]>);
  }, [interviewData, formatDateKey]);

  const buttonHandler = () => {
    const schedulingDefault = email_template.scheduling.defaultJson;
    const meetingDefault = email_template.meetingDetails.defaultJson;
    const schedulingJson = JSON.stringify(schedulingDefault);
    const meetingDetailsJson = JSON.stringify(meetingDefault);
    const schedulingHtml = renderTipTapJsonToHtml(schedulingDefault);
    const meetingDetailsHtml = renderTipTapJsonToHtml(meetingDefault);

    // Only proceed with update/create if we have valid panelists
    if (!panelists || panelists.length === 0) {
      return;
    }

    if (interviewData && interviewData.id) {
      updateInterview(
        schedulingHtml,
        schedulingJson,
        meetingDetailsHtml,
        meetingDetailsJson,
      );
    } else {
      createInterview().catch((err) => {
        console.error('Failed to create interview:', err);
      });
    }
  };
  return {
    createInterview,
    updateInterview,
    title,
    setTitle,
    jobOpeningId,
    setJobOpeningId,
    defaultEmployeeUserOptions,
    handleHostChange,
    loadOptionsDebounced,
    handleDateSelection,
    handleTimeSlotSelection,
    selectedDates,
    selectedTimes,
    host,
    panelists,
    interviewData,
    resetForm: () => {
      setInterviewData(null);
      setTitle('');
      setHost(undefined);
      setPanelists([]);
      setSelectedDates([]);
      setSelectedTimes({});
      setIsChanged(false);
    },
    isFormValid: useMemo(() => {
      const hasTime = Object.values(selectedTimes).some(
        (times) => times.length > 0,
      );
      return !!(
        title.trim() &&
        host &&
        panelists.length > 0 &&
        selectedDates.length > 0 &&
        hasTime
      );
    }, [title, host, panelists, selectedDates, selectedTimes]),
    isChanged,
    handleInputChange: (field?: keyof typeof touched) => {
      setIsChanged(true);
      if (field) {
        setTouched((prev) => ({ ...prev, [field]: true }));
      }
      validateForm();
    },
    handleBlur: (field: keyof typeof touched) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validateForm();
    },
    touched,
    errors,
    validateForm,
    generateTime,
    formatDateKey,
    handlePanelists,
    disabledSlots,
    buttonHandler,
  };
};
